import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { Heart, MessageSquare, Send } from "lucide-react";

interface Media {
  id: string;
  file_url: string;
  type: "image" | "video";
}

interface Comment {
  id: string;
  post_id: string;
  content: string;
  parent_id: string | null;
  created_at: string;
  author: {
    first_name: string | null;
    last_name: string | null;
    email: string;
    role: string;
  };
  children?: Comment[];
}

interface Post {
  id: string;
  content: string;
  created_at: string;
  author_id: string;
  media: Media[];
  likes: number;
  likedByMe: boolean;
  currentIndex: number;
  author: {
    first_name: string | null;
    last_name: string | null;
    email: string;
    role: string;
  };
  comments: Comment[];
}

export function FeedTab() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentInput, setCommentInput] = useState<Record<string, string>>({});
  const [replyInput, setReplyInput] = useState<Record<string, string>>({});
  const [showReply, setShowReply] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadPosts();
  }, []);

  async function loadPosts() {
    setLoading(true);

    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;

    const { data } = await supabase
      .from("posts")
      .select(`
        *,
        profiles:author_id(first_name,last_name,email,role),
        post_media(*),
        post_likes(user_id),
        post_comments(
          *,
          profiles:user_id(first_name,last_name,email,role)
        )
      `)
      .order("created_at", { ascending: false });

    if (!data) {
      setLoading(false);
      return;
    }

    const formatted = data.map((p: any) => ({
      id: p.id,
      content: p.content,
      created_at: p.created_at,
      author_id: p.author_id,
      media: p.post_media || [],
      likes: p.post_likes?.length || 0,
      likedByMe: p.post_likes?.some((l: any) => l.user_id === userId),
      currentIndex: 0,
      author: p.profiles,
      comments: (p.post_comments || []).map((c: any) => ({
        id: c.id,
        post_id: c.post_id,
        content: c.content,
        parent_id: c.parent_id,
        created_at: c.created_at,
        author: c.profiles,
      })),
    }));

    setPosts(formatted);
    setLoading(false);
  }

  /* ===========================
     BUILD INFINITE COMMENT TREE
  ============================ */

  function buildCommentTree(comments: Comment[]): Comment[] {
    const map = new Map<string, Comment>();
    const roots: Comment[] = [];

    comments
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() -
          new Date(a.created_at).getTime()
      )
      .forEach((comment) => {
        map.set(comment.id, { ...comment, children: [] });
      });

    map.forEach((comment) => {
      if (comment.parent_id) {
        const parent = map.get(comment.parent_id);
        if (parent) {
          parent.children!.push(comment);
        }
      } else {
        roots.push(comment);
      }
    });

    return roots;
  }

  async function toggleLike(postId: string, liked: boolean) {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return;

    if (liked) {
      await supabase
        .from("post_likes")
        .delete()
        .eq("post_id", postId)
        .eq("user_id", userId);
    } else {
      await supabase.from("post_likes").insert({
        post_id: postId,
        user_id: userId,
      });
    }

    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? {
              ...p,
              likedByMe: !liked,
              likes: liked ? p.likes - 1 : p.likes + 1,
            }
          : p
      )
    );
  }

  async function addComment(postId: string, parentId?: string) {
    const content = parentId
      ? replyInput[parentId]
      : commentInput[postId];

    if (!content?.trim()) return;

    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return;

    const { data: inserted } = await supabase
      .from("post_comments")
      .insert({
        post_id: postId,
        user_id: userId,
        content,
        parent_id: parentId || null,
      })
      .select(`
        *,
        profiles:user_id(first_name,last_name,email,role)
      `)
      .single();

    if (!inserted) return;

    const newComment: Comment = {
      id: inserted.id,
      post_id: inserted.post_id,
      content: inserted.content,
      parent_id: inserted.parent_id,
      created_at: inserted.created_at,
      author: inserted.profiles,
    };

    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, comments: [newComment, ...p.comments] }
          : p
      )
    );

    setCommentInput((prev) => ({ ...prev, [postId]: "" }));
    setReplyInput((prev) => ({ ...prev, [parentId || ""]: "" }));
    setShowReply((prev) => ({ ...prev, [parentId || ""]: false }));
  }

  function renderComments(
    comments: Comment[],
    postId: string,
    level = 0
  ) {
    return comments.map((comment) => (
      <div
        key={comment.id}
        className="mt-3 text-sm"
        style={{ marginLeft: level * 20 }}
      >
        <div className="font-medium">
          {comment.author.role === "admin" ||
          comment.author.role === "superadmin"
            ? "Talis'on Services"
            : `${comment.author.first_name || ""} ${
                comment.author.last_name || ""
              }`}
        </div>

        <div>{comment.content}</div>

        <button
          onClick={() =>
            setShowReply((prev) => ({
              ...prev,
              [comment.id]: !prev[comment.id],
            }))
          }
          className="text-xs text-blue-600 mt-1"
        >
          Reply
        </button>

        {showReply[comment.id] && (
          <div className="flex gap-2 mt-2">
            <input
              type="text"
              value={replyInput[comment.id] || ""}
              onChange={(e) =>
                setReplyInput({
                  ...replyInput,
                  [comment.id]: e.target.value,
                })
              }
              placeholder="Write a reply..."
              className="flex-1 border rounded px-2 py-1 text-sm"
            />
            <button
              onClick={() => addComment(postId, comment.id)}
              className="bg-blue-600 text-white px-2 py-1 rounded text-xs"
            >
              Send
            </button>
          </div>
        )}

        {comment.children &&
          renderComments(comment.children, postId, level + 1)}
      </div>
    ));
  }

  if (loading) return <div className="p-8 text-center">Loading feed...</div>;

  return (
    <div className="space-y-6 p-6">
      <h1 className="text-2xl font-bold">Community Feed</h1>

      {posts.map((post) => (
        <div key={post.id} className="bg-white rounded-xl shadow p-4 space-y-4">

          <div>
            <div className="font-semibold">
              {post.author.role === "admin" ||
              post.author.role === "superadmin"
                ? "Talis'on Services"
                : `${post.author.first_name || ""} ${
                    post.author.last_name || ""
                  }`}
            </div>
            <div className="text-xs text-gray-500">
              {new Date(post.created_at).toLocaleString("en-GB", {
                hour12: false,
              })}
            </div>
          </div>

          <div>{post.content}</div>

          {post.media.length > 0 && (
            <div className="relative flex justify-center">
              {post.media[post.currentIndex].type === "image" ? (
                <img
                  src={post.media[post.currentIndex].file_url}
                  className="max-h-[500px] object-contain rounded-lg"
                />
              ) : (
                <video
                  src={post.media[post.currentIndex].file_url}
                  controls
                  className="max-h-[500px] object-contain rounded-lg"
                />
              )}
            </div>
          )}

          <div className="flex gap-6 items-center">
            <button
              onClick={() => toggleLike(post.id, post.likedByMe)}
              className={`flex items-center gap-2 ${
                post.likedByMe
                  ? "text-blue-600"
                  : "text-gray-500 hover:text-blue-600"
              }`}
            >
              <Heart
                className={`h-5 w-5 ${
                  post.likedByMe ? "fill-blue-600 text-blue-600" : ""
                }`}
              />
              {post.likes}
            </button>

            <div className="flex items-center gap-2 text-gray-500">
              <MessageSquare className="h-5 w-5" />
              {post.comments.length}
            </div>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={commentInput[post.id] || ""}
              onChange={(e) =>
                setCommentInput({
                  ...commentInput,
                  [post.id]: e.target.value,
                })
              }
              placeholder="Write a comment..."
              className="flex-1 border rounded px-3 py-2 text-sm"
            />
            <button
              onClick={() => addComment(post.id)}
              className="bg-blue-600 text-white px-3 py-2 rounded"
            >
              <Send size={16} />
            </button>
          </div>

          {renderComments(buildCommentTree(post.comments), post.id)}
        </div>
      ))}
    </div>
  );
}